import React, {
  useRef,
  useLayoutEffect,
  useCallback,
  MouseEvent,
  useState,
} from 'react'
import { withRouter, RouteComponentProps } from 'react-router-dom'

// Utils
import usePrevious from 'utils/hooks/usePrevious'
import {
  createSrcSetForFiles,
  cursor,
  scrollOffset,
  shrinkToFitFactor,
  avoidBrowserBackActionWhenPanning,
} from './utils'
import { getArtboardThumbnail } from 'modules/shares/utils/getArtboardThumbnail'

// Components
import { ZoomTypes, ZoomProps } from 'components/Zoom'

// Hooks
import useScaleArtboard from './useScaleArtboard'
import { useVersioning } from 'modules/versioning'

// Styles
import {
  Container,
  ScrollArea,
  ArtboardContainer,
  ArtboardImage,
  ArtboardInspector,
  SCROLL_PADDING,
} from './ArtboardView.styles'

// Types
import { InspectorState } from 'modules/shares/Inspector/types'
import { ArtboardDetailInfoFragment } from 'gqlTypes'

type Artboard = Pick<
  ArtboardDetailInfoFragment,
  'width' | 'height' | 'files' | 'isPage' | 'name' | 'hasMissingFonts' | 'uuid'
>

export type ArtboardFile = NonNullable<NonNullable<Artboard['files']>[0]>

interface ArtboardViewProps extends RouteComponentProps {
  artboard?: Artboard
  currentZoom: ZoomTypes
  onZoomChanged: ZoomProps['onZoomChanged']
  inspector: InspectorState
  shareIdentifier: string
  permanentArtboardShortId?: string
}

// Globals
const IS_SAFARI = window.safari !== undefined
const FIT_TO_SCREEN_PADDING = 20

const ArtboardView = (props: ArtboardViewProps) => {
  const {
    artboard,
    currentZoom,
    onZoomChanged,
    inspector,
    shareIdentifier,
    permanentArtboardShortId,
    ...rest
  } = props

  const [isScrollable, setIsScrollable] = useState({
    horizontal: false,
    vertical: false,
  })
  const scrollRef = useRef<HTMLDivElement>()
  const scroll = useRef({ top: 0, left: 0 })
  const previousZoom = usePrevious(currentZoom)
  const zoomPoint = useRef({
    top: 0,
    left: 0,
  })
  const { artboardThumbnail } = useVersioning()

  const artboardSize = {
    width: artboard?.width ?? 0,
    height: artboard?.height ?? 0,
  }

  const [measureRef, scaledArtboardSize, containerSize] = useScaleArtboard({
    artboardSize,
    currentZoom,
    onZoomChanged,
  })

  const containerRef = useCallback(
    (element: HTMLDivElement) => {
      scrollRef.current = element
      measureRef(element)

      if (element !== null && !IS_SAFARI) {
        element.addEventListener('wheel', avoidBrowserBackActionWhenPanning)

        return () => {
          element.removeEventListener(
            'wheel',
            avoidBrowserBackActionWhenPanning
          )
        }
      }
    },
    [scrollRef, measureRef]
  )

  useLayoutEffect(() => {
    if (!scroll.current || !scrollRef.current) {
      return
    }

    const artboardView = document.getElementById('ArtboardView')

    let scrollTop = 0
    let scrollLeft = 0

    const currentTop = scroll.current.top < 0 ? 0 : scroll.current.top
    const currentLeft = scroll.current.left < 0 ? 0 : scroll.current.left

    // reset the scrolling after adding the margin
    // the check for the negative values in scroll.current is for Safari
    // it gets set as negative on very specific situations and messes up the math
    if (inspector.isActive) {
      scrollTop = currentTop + SCROLL_PADDING
      scrollLeft = currentLeft + SCROLL_PADDING
    } else {
      scrollTop = currentTop - SCROLL_PADDING
      scrollLeft = currentLeft - SCROLL_PADDING
    }

    scrollTop = scrollTop < 0 ? 0 : scrollTop
    scrollLeft = scrollLeft < 0 ? 0 : scrollLeft

    if (artboardView) {
      const isScrollHorizontal =
        artboardView?.scrollWidth > artboardView?.clientWidth
      const isScrollVertical =
        artboardView?.scrollHeight > artboardView?.clientHeight

      if (isScrollVertical) {
        scroll.current.top = scrollTop
        artboardView.scrollTop = scrollTop
        scrollRef.current.scrollTop = scrollTop
      }

      if (isScrollHorizontal) {
        scroll.current.left = scrollLeft
        scrollRef.current.scrollLeft = scrollLeft
        artboardView.scrollLeft = scrollLeft
      }

      setIsScrollable({
        horizontal: isScrollHorizontal,
        vertical: isScrollVertical,
      })
    }
  }, [scroll, scrollRef, currentZoom, inspector.isActive])

  const handleContainerClick = (event: MouseEvent<HTMLDivElement>) => {
    if (inspector.isActive) {
      inspector.onClickLayer(undefined)
      return
    } else {
      if (currentZoom.type === 'ZOOM_TO_FIT' && currentZoom.factor === 1) {
        return
      }

      if (currentZoom.type !== 'ZOOM_TO_FIT') {
        onZoomChanged({ type: 'ZOOM_TO_FIT' })
      } else {
        const target = event.currentTarget as HTMLElement
        const clickOffset = {
          top: event.clientY - target.getBoundingClientRect().top,
          left: event.clientX - target.getBoundingClientRect().left,
        }

        zoomPoint.current = {
          top: clickOffset.top,
          left: clickOffset.left,
        }
        onZoomChanged({ type: 'ZOOM_FACTOR', factor: 1 })
      }
    }
  }

  const files = (artboard?.files.filter(x => !!x) as ArtboardFile[]) || []

  const zoomToFitFactor = shrinkToFitFactor({
    containerSize: {
      width: containerSize.width - FIT_TO_SCREEN_PADDING * 2,
      height: containerSize.height - FIT_TO_SCREEN_PADDING * 2,
    },
    itemSize: artboardSize,
  })

  if (
    scrollRef.current &&
    typeof currentZoom.factor === 'number' &&
    typeof previousZoom!.factor === 'number'
  ) {
    const scaleFactor = currentZoom.factor / previousZoom!.factor

    scroll.current = scrollOffset({
      scaleFactor,
      scaledSize: scaledArtboardSize,
      containerSize,
      currentOffset: scroll.current,
      centerPoint: zoomPoint.current,
    })

    zoomPoint.current = {
      top: 0,
      left: 0,
    }
  }

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const target = event && (event.target as Element)
    if (!target || currentZoom.type === 'ZOOM_TO_FIT') {
      return
    }

    // Target will return scroll left as 0 when zooming in the artboard
    // which impacts how the scrollOffset is calculated so we're
    // using the last scroll value in those cases.
    const scrollTop = target.scrollTop || scroll.current.top
    const scrollLeft = target.scrollLeft || scroll.current.left

    scroll.current = { top: scrollTop, left: scrollLeft }
  }

  const isPage = artboard?.isPage ?? false

  /**
   * Adding a key to prevent the case where the artboard changes but the
   * image component does not refresh in time, resulting in a glitch
   * where you could see the previous artboard thumbnail when navigating back and forth
   **/
  const artboardImageKey = artboard?.uuid

  const getThumbnail = () => {
    if (artboardThumbnail !== '') {
      return artboardThumbnail
    }

    return artboard
      ? getArtboardThumbnail(artboard as ArtboardDetailInfoFragment)
      : undefined
  }

  return (
    <Container
      id="ArtboardView" // used to get the scroll position for the ContextMenu
      data-testid="artboardview"
      ref={containerRef}
      cursor={cursor({
        inspectorActive: inspector.isActive,
        zoom: currentZoom,
        zoomToFitFactor,
      })}
      onClick={handleContainerClick}
      onScroll={handleScroll}
      {...rest}>
      <ScrollArea
        itemWidth={artboardSize.width}
        itemHeight={artboardSize.height}
        containerWidth={containerSize.width}
        containerHeight={containerSize.height}
        isInspectorActive={inspector.isActive}
        isScrollableHorizontal={isScrollable.horizontal}
        isScrollableVertical={isScrollable.vertical}
        scaledArtboardSizeWidth={scaledArtboardSize.width}
        scaledArtboardSizeHeight={scaledArtboardSize.height}>
        <ArtboardContainer
          isPage={isPage}
          width={scaledArtboardSize.width}
          height={scaledArtboardSize.height}>
          <ArtboardImage
            key={artboardImageKey}
            alt={artboard?.name ?? ''}
            isPage={isPage}
            zoomFactor={currentZoom.factor}
            src={files?.[0]?.url ?? undefined}
            thumbnail={getThumbnail()}
            height={scaledArtboardSize.height}
            width={scaledArtboardSize.width}
            srcSet={createSrcSetForFiles(files) ?? undefined}
          />
          {inspector.isActive && (
            <ArtboardInspector
              zoomFactor={currentZoom.factor ?? 1}
              inspector={inspector}
              shareIdentifier={shareIdentifier}
              permanentArtboardShortId={permanentArtboardShortId}
            />
          )}
        </ArtboardContainer>
      </ScrollArea>
    </Container>
  )
}

export default withRouter(ArtboardView)
